import React, { createContext, useContext } from "react";
import { database } from "./firebase-config";
import {
	collection,
	doc,
	getDocs,
	getDoc,
	addDoc,
	updateDoc,
	deleteDoc,
	serverTimestamp,
	query,
	orderBy,
	where,
} from "firebase/firestore";

const FirebaseContext = createContext();

const useFirebase = () => {
	return useContext(FirebaseContext);
};

const FirebaseProvider = ({ children }) => {
	const value = {
		database,
		getAllPBTasks: async () => {
			try {
				const collectionRef = collection(database, "product-backlog");

				const data = await getDocs(collectionRef);
				const filteredData = data.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return filteredData;
			} catch (error) {
				console.error(error);
			}
		},

		addPBTask: async (newTask) => {
			const collectionRef = collection(database, "product-backlog");

			try {
				const timeStamp = serverTimestamp();
				const taskToSave = {
					...newTask,
					createdAt: timeStamp,
				};
				await addDoc(collectionRef, taskToSave);
			} catch (error) {
				console.error(error);
			}
		},

		editPBTask: async (taskID, editedTask) => {
			const taskDoc = doc(database, "product-backlog", taskID);
			await updateDoc(taskDoc, editedTask);
		},

		deletePBTask: async (taskID) => {
			const taskDoc = doc(database, "product-backlog", taskID);
			await deleteDoc(taskDoc);
		},

		sortPBTasks: async (priority) => {
			try {
				const collectionRef = collection(database, "product-backlog");
				let queryTask = undefined;
				if (priority === "lowToUrgent") {
					queryTask = query(
						collectionRef,
						orderBy("taskPriority", "asc")
					);
				} else if (priority === "urgentToLow") {
					queryTask = query(
						collectionRef,
						orderBy("taskPriority", "desc")
					);
				} else if (priority === "recentToOldest") {
					queryTask = query(
						collectionRef,
						orderBy("createdAt", "desc")
					);
				} else {
					queryTask = query(
						collectionRef,
						orderBy("createdAt", "asc")
					);
				}

				const sortedData = await getDocs(queryTask);
				const sortedTasks = sortedData.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return sortedTasks;
			} catch (err) {
				console.error(err);
			}
		},

		filterByTaskTag: async (categoryTag) => {
			try {
				const collectionRef = collection(database, "product-backlog");
				const queryTask = query(
					collectionRef,
					where("taskTag", "array-contains", categoryTag)
				);
				const data = await getDocs(queryTask);
				const filteredTasks = data.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return filteredTasks;
			} catch (err) {
				console.error(err);
			}
		},

		getAllSprints: async () => {
			try {
				const collectionRef = collection(database, "sprints");
				const data = await getDocs(collectionRef);
				const filteredData = data.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return filteredData;
			} catch (error) {
				console.error(error);
			}
		},

		addSprint: async (newSprint) => {
			const collectionRef = collection(database, "sprints");

			try {
				const timeStamp = serverTimestamp();
				const sprintToSave = {
					...newSprint,
					createdAt: timeStamp,
				};
				await addDoc(collectionRef, sprintToSave);
			} catch (error) {
				console.error(error);
			}
		},

		editSprint: async (sprintID, editedSprint) => {
			const sprintDoc = doc(database, "sprints", sprintID);
			await updateDoc(sprintDoc, editedSprint);
		},

		deleteSprint: async (sprintID) => {
			const sprintDoc = doc(database, "sprints", sprintID);
			await deleteDoc(sprintDoc);
		},

		getAllSprintTask: async (sprintID) => {
			try {
				const collectionRef = collection(database, "sprint-tasks");

				const data = await getDocs(collectionRef);
				const filteredData = data.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return filteredData.filter(
					(task) => task.sprintID === sprintID
				);
			} catch (error) {
				console.error(error);
			}
		},

		addTaskToSB: async (newSprintTask) => {
			const sTColRef = collection(database, "sprint-tasks");

			try {
				const taskDoc = doc(
					database,
					"product-backlog",
					newSprintTask.taskID
				);
				const taskData = (await getDoc(taskDoc)).data();

				const timeStamp = serverTimestamp();
				const newSprintTaskToAdd = {
					...taskData,
					sprintID: newSprintTask.sprintID,
					userID: newSprintTask.userID,
					sprintLogTime: newSprintTask.sprintLogTime,
					sprintTotalLogTime: newSprintTask.sprintTotalLogTime,
					sprintCompletionDate: newSprintTask.sprintCompletionDate,
					createdAt: timeStamp,
				};
				await addDoc(sTColRef, newSprintTaskToAdd);
				await deleteDoc(taskDoc);
			} catch (error) {
				console.error(error);
			}
		},

		editSprintTask: async (sprintTaskID, editedSprintTask) => {
			const sprintTaskDoc = doc(database, "sprint-tasks", sprintTaskID);
			await updateDoc(sprintTaskDoc, editedSprintTask);
		},

		removeSprintTask: async (sprintTask) => {
			const sprintTaskDoc = doc(database, "sprint-tasks", sprintTask.id);
			await deleteDoc(sprintTaskDoc);

			const collectionRef = collection(database, "product-backlog");

			try {
				const timeStamp = serverTimestamp();
				const taskToSave = {
					taskName: sprintTask.taskName,
					taskType: sprintTask.taskType,
					taskStoryPoint: sprintTask.taskStoryPoint,
					taskTag: sprintTask.taskTag,
					taskPriority: sprintTask.taskPriority,
					taskDescription: sprintTask.taskDescription,
					taskStatus: sprintTask.taskStatus,
					taskStage: sprintTask.taskStage,
					taskAssignee: sprintTask.taskAssignee,
					taskInSprint: false,
					createdAt: timeStamp,
				};
				await addDoc(collectionRef, taskToSave);
			} catch (error) {
				console.error(error);
			}
		},

		sortSprintTasks: async (priority) => {
			try {
				const collectionRef = collection(database, "sprint-tasks");
				let queryTask = undefined;
				if (priority === "Low to Urgent") {
					queryTask = query(
						collectionRef,
						orderBy("taskPriority", "asc")
					);
				} else if (priority === "Urgent to Low") {
					queryTask = query(
						collectionRef,
						orderBy("taskPriority", "desc")
					);
				} else if (priority === "Recent to Oldest") {
					queryTask = query(
						collectionRef,
						orderBy("createdAt", "desc")
					);
				} else {
					queryTask = query(
						collectionRef,
						orderBy("createdAt", "asc")
					);
				}

				const sortedData = await getDocs(queryTask);
				const sortedTasks = sortedData.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				return sortedTasks;
			} catch (err) {
				console.error(err);
			}
		},

		filterBySprintTaskTag: async (categoryTag) => {
			try {
				const collectionRef = collection(database, "sprint-tasks");
				const queryTask = query(
					collectionRef,
					where("taskTag", "array-contains", categoryTag)
				);
				const data = await getDocs(queryTask);
				const filteredTasks = data.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				console.log(filteredTasks);
				return filteredTasks;
			} catch (err) {
				console.error(err);
			}
		},

		getSprintData: async (sprintID) => {
			try {
				// process sprint data
				const sprintDoc = doc(database, "sprints", sprintID);
				const sprintRawData = (await getDoc(sprintDoc)).data();

				const sprintTasksColRef = collection(database, "sprint-tasks");
				const queryTask = query(
					sprintTasksColRef,
					where("sprintID", "==", sprintID)
				);
				const rawSprintTasksData = (await getDocs(queryTask)).docs.map(
					(doc) => ({
						...doc.data(),
						id: doc.id,
					})
				);

				// date diff between start and end date
				const { sprintStartDate, sprintEndDate } = sprintRawData;
				const differenceInMilliseconds =
					sprintEndDate.toDate() - sprintStartDate.toDate();
				const sprintLength = Math.floor(
					differenceInMilliseconds / (24 * 60 * 60 * 1000)
				);
				// days data array (for labeling x-axis)
				const sprintDays = Array.from(
					{ length: sprintLength + 1 },
					(_, index) => index
				);
				// initial total number of story points
				const sprintTotalStoryPoints = rawSprintTasksData.reduce(
					(accum, curr) => {
						return accum + parseInt(curr.taskStoryPoint);
					},
					0
				);
				// expected array
				const expectedDailySP = sprintTotalStoryPoints / sprintLength;
				let expectedSPData = [];
				for (let i = 0; i <= sprintLength; i++) {
					expectedSPData.push(
						Math.floor(sprintTotalStoryPoints - i * expectedDailySP)
					);
				}

				// actual data array
				const actualSPData = value.getSprintDataAux(
					rawSprintTasksData,
					sprintStartDate,
					sprintEndDate,
					sprintTotalStoryPoints
				);

				const sprintData = {
					id: sprintRawData.id,
					...sprintRawData,
					sprintDays: sprintDays,
					expectedSPData,
					actualSPData,
				};
				return sprintData;
			} catch (err) {
				console.error(err);
			}
		},

		getSprintDataAux: (
			data,
			sprintStartDate,
			sprintEndDate,
			sprintTotalStoryPoints
		) => {
			const dateMap = {};
			let initialSumSP = sprintTotalStoryPoints;

			// Initialise dateMap with 0 for each day within the sprint period
			const currentDate = new Date(sprintStartDate.toMillis());
			while (currentDate <= sprintEndDate.toDate()) {
				const formattedDate = currentDate.toISOString().split("T")[0];
				dateMap[formattedDate] = 0;
				currentDate.setDate(currentDate.getDate() + 1);
			}

			// fill in the dateMap and sum the total storyPoints (similar concept to counting sort)
			data.filter((task) => task.taskStatus === "Completed").forEach(
				({ sprintCompletionDate, taskStoryPoint }) => {
					dateMap[sprintCompletionDate] += parseInt(taskStoryPoint);
				}
			);

			const result = [initialSumSP];
			for (const [sprintCompletionDate, taskStoryPoint] of Object.entries(
				dateMap
			)) {
				result.push(initialSumSP - taskStoryPoint);
				initialSumSP = initialSumSP - taskStoryPoint;
			}
			return result;
		},

		getUser: async (userName, userPassword) => {
			try {
				const collectionRef = collection(database, "users");
				const queryUser = query(
					collectionRef,
					where("userName", "==", userName),
					where("userPassword", "==", userPassword)
				);
				const data = await getDocs(queryUser);
				const userData = data.docs[0].data();
				return userData;
			} catch (err) {
				console.error(err);
				return undefined;
			}
		},

		addUser: async (newUser) => {
			const collectionRef = collection(database, "users");

			try {
				const timeStamp = serverTimestamp();
				const userToSave = {
					...newUser,
					createdAt: timeStamp,
				};
				await addDoc(collectionRef, newUser);
			} catch (error) {
				console.error(error);
			}
		},

		updateUserPassword: async (user, newPassword) => {
			const userName = user?.userName;
			const oldPassword = user?.userPassword;
			try {
				const collectionRef = collection(database, "users");
				const queryUser = query(
					collectionRef,
					where("userName", "==", userName),
					where("userPassword", "==", oldPassword)
				);
				const data = await getDocs(queryUser);
				const userID = data.docs[0].id;

				const userDoc = doc(database, "users", userID);
				const updatedUser = {
					...user,
					userPassword: newPassword,
				};
				await updateDoc(userDoc, updatedUser);
			} catch (err) {
				console.error(err);
			}
		},

		removeUser: async (userID) => {
			const userDoc = doc(database, "users", userID);
			await deleteDoc(userDoc);
		},

		getAllUsersData: async () => {
			try {
				// get all users in whole project
				const userColRef = collection(database, "users");
				const rawUserData = await getDocs(userColRef);
				const filteredUserData = rawUserData.docs.map((userDoc) => ({
					...userDoc.data(),
					id: userDoc.id,
				}));

				// get all sprint tasks in while project
				const sprintTaskColRef = collection(database, "sprint-tasks");
				const rawSprintTaskData = await getDocs(sprintTaskColRef);
				const filteredSprintTaskData = rawSprintTaskData.docs.map(
					(userDoc) => ({
						...userDoc.data(),
						id: userDoc.id,
					})
				);

				// for each user
				const allUserData = filteredUserData.map((currUser) => {
					const userTasksArr = filteredSprintTaskData.filter(
						(task) => task.taskAssignee === currUser.userName
					);

					const userMap = {};
					let userTotalWorkingTime = 0;
					userTasksArr.map((task) => {
						task.sprintLogTime.map((time) => {
							userMap[time.day] = isNaN(userMap[time.day]) ? parseInt(time.hour) : userMap[time.day] + parseInt(time.hour);
							return 0;
						});
						userTotalWorkingTime += task.sprintTotalLogTime;
						return 0;
					});
					const userInfo = {
						userName: currUser.userName,
						userAvgTime:
							userTotalWorkingTime / Object.keys(userMap).length,
						userWorkingDate: Object.keys(userMap),
						userTotalWorkingHour: userTotalWorkingTime,
						userWorkingHour: Object.values(userMap),
					};
					return userInfo;
				});
				return allUserData;
			} catch (err) {
				console.error(err);
			}
		},
	};

	return (
		<FirebaseContext.Provider value={value}>
			{children}
		</FirebaseContext.Provider>
	);
};

export { useFirebase, FirebaseProvider };
